import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useProjects } from '../../hooks/useProjects.jsx';
import { formatDate, isActiveProject } from '../../utils/helpers';
import StatCard from '../common/StatCard.jsx'; // Updated import path
import { BarChart3, Users, AlertTriangle, CheckCircle, Calendar, Plus, TrendingUp } from 'lucide-react';
import {
  ProjectsByStatusPie, BudgetUtilizationBar, PriorityDistributionBar, ProjectProgressLine,
} from '../common/DashboardCharts.jsx';
import AIActionGuide from '../ai/AIActionGuide.jsx';
import AgentProposals from './AgentProposals.jsx'
import ReadyToCloseAlert from './ReadyToCloseAlert.jsx';
import { useTranslation } from 'react-i18next';

// Helper for project status colors - REMOVED all "dark:" classes
const projectStatusColors = {
  'In Progress': 'bg-green-100 text-green-800',
  'Pending Approval': 'bg-yellow-100 text-yellow-800',
  'Planning': 'bg-blue-100 text-blue-800',
  'Completed': 'bg-gray-100 text-gray-800',
  'At Risk': 'bg-red-100 text-red-800',
};

const ProjectManagerDashboard = () => {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const { user: currentUser } = useAuth(); // Get current user from useAuth

  // useProjects() already filters projects for the current user's role (PM)
  const { projects: pmProjects, loading: projectsLoading } = useProjects();

  // Dashboard stats calculations - adapted to mockData.js structure
  const dashboardStats = useMemo(() => {
    const list = Array.isArray(pmProjects) ? pmProjects : [];
    if (list.length === 0) return { totalProjects: 0, activeProjects: 0, overdueProjects: 0, teamSize: 0 };

    const totalProjects = list.length;
    // Active means the same thing here as on the Admin dashboard and in the financial
    // reports: open work, whether it has started or is still being planned. This counted
    // only 'In Progress', so a PM and an Admin looking at the same projects read two
    // different numbers off two screens.
    const activeProjects = list.filter(isActiveProject).length;

    const now = new Date();
    const overdueProjects = list.filter(p =>
      p.endDate && new Date(p.endDate) < now &&
      !['Completed', 'Archived'].includes(p.status)
    ).length;  // only count projects that actually have a past end date

    // Consultants are assigned per MILESTONE, and one project can carry several. Counting the
    // project's single consultant_id missed everybody but the first, and missed them entirely
    // until a contract was approved -- so a PM running a four-person project read "1".
    const teamSize = new Set(
      list.flatMap(p => (p.consultantNames?.length ? p.consultantNames : [p.consultant]))
          .filter(Boolean)
    ).size;

    return {
      totalProjects,
      activeProjects,
      overdueProjects,
      teamSize
    };
  }, [pmProjects]);

  // Chart data - adapted to mockData.js structure
  // Chart data is derived inside the shared chart components now — see the charts block below.

  // Upcoming deadlines - adapted to mockData.js structure
  const upcomingdeadlines = useMemo(() => {
    const list = Array.isArray(pmProjects) ? pmProjects : [];
    if (list.length === 0) return [];
    const today = new Date();
    return list
      .filter(project => {
        const deadline = new Date(project.endDate);
        const daysUntildeadline = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntildeadline <= 7 && daysUntildeadline >= 0 && project.status !== 'Completed';
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
  }, [pmProjects]);

  // Team Overview Logic
  // Team Overview Logic
  const teamMembers = useMemo(() => {
    const list = Array.isArray(pmProjects) ? pmProjects : [];
    if (list.length === 0) return [];

    // Same reason as teamSize: the people are on the milestones, so the team panel was showing
    // one name for a project several consultants were working on.
    const uniqueConsultants = new Map();
    list.forEach(p => {
      const names = p.consultantNames?.length ? p.consultantNames : (p.consultant ? [p.consultant] : []);
      names.forEach(name => {
        if (!uniqueConsultants.has(name)) {
          uniqueConsultants.set(name, { id: name, name, role: 'Consultant', status: 'Active' });
        }
      });
    });
    return Array.from(uniqueConsultants.values());
  }, [pmProjects]);



  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    // Order (Mam #18): AI tasks → recent projects + quick actions → everything else.
    // Using flex order so the big blocks don't have to move in the markup.
    <div className="flex flex-col gap-6 p-6">
      {/* AI Copilot action guide */}
      <div className="order-1"><AIActionGuide />
      {/* Prepared by the agent, not yet done — usually from an email. */}
      <AgentProposals /></div>
      {/* Projects whose milestones are all complete — prompt to close them */}
      <div className="order-1"><ReadyToCloseAlert /></div>
      {/* Dashboard header */}
      <div className="order-2 flex flex-wrap items-center justify-between gap-4">
        <div>
          {/* Removed "dark:text-white" */}
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {currentUser?.full_name || currentUser?.name}
          </h1>
          {/* Removed "dark:text-gray-400" */}
          <p className="text-gray-600">
            Here's your project management overview
          </p>
        </div>

      </div>

      {/* Key Stats Overview */}
      <div className="order-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('total_projects')}
          value={dashboardStats.totalProjects}
          icon={BarChart3}
          color="blue"
        />
        <StatCard
          title={t('active_projects')}
          value={dashboardStats.activeProjects}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title={t('overdue_projects')}
          value={dashboardStats.overdueProjects}
          icon={AlertTriangle}
          color="red"
        />
        <StatCard
          title={t('team_members')}
          value={dashboardStats.teamSize}
          icon={Users}
          color="purple"
        />
      </div>

      {/* Charts. These were written out by hand here, and nowhere else had them — which is why
          the PM view looked like the fuller picture of data an Admin also owns. They are now the
          shared ones every other dashboard already used, so the same projects cannot produce two
          different pictures. The budget bar in particular used to plot the budget REQUESTED
          against spend; the shared one plots what was APPROVED, which is what the Admin total is
          a sum of. */}
      <div className="order-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProjectsByStatusPie projects={pmProjects} />
        <BudgetUtilizationBar projects={pmProjects} />
        <PriorityDistributionBar projects={pmProjects} />
        <ProjectProgressLine projects={pmProjects} />
      </div>

      {/* Main Dashboard Grid — recent projects + quick actions (Mam #18: near the top) */}
      <div className="order-3 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Project Overview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Projects */}
          {/* Removed "dark:bg-gray-800" */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              {/* Removed "dark:text-white" */}
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Projects
              </h3>
              <button
                onClick={() => navigate('/projects')} // Use navigate
                // Removed "dark:text-blue-400"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View All
              </button>
            </div>
            {pmProjects.length === 0 ? (
              // Removed "dark:text-gray-400"
              <p className="text-gray-600">{t('no_projects_assigned')}</p>
            ) : (
              <div className="space-y-3">
                {pmProjects.slice(0, 5).map(project => (
                  <div
                    key={project.id}
                    onClick={() => project.id && navigate(`/projects/${project.id}`)}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-3 cursor-pointer hover:bg-gray-100"
                    title={t('open_project')}
                  >
                    <div className="flex-1 w-full">
                      <p className="font-medium text-gray-900 hover:text-blue-600">{project.name}</p>
                      <p className="text-sm text-gray-600">
                        Due: {formatDate(project.endDate)}
                      </p>
                      <div className="flex items-center mt-1">
                        {/* Removed "dark:bg-gray-600" */}
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        {/* Removed "dark:text-gray-400" */}
                        <span className="text-sm text-gray-600 min-w-[40px]">
                          {project.progress}%
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full w-fit ${projectStatusColors[project.status] || projectStatusColors['Completed']
                      }`}>
                      {project.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming deadlines */}
          {/* Removed "dark:bg-gray-800" */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            {/* Removed "dark:text-white" */}
            <h3 className="text-lg font-semibold mb-4 text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-500" />
              Upcoming deadlines
            </h3>
            {upcomingdeadlines.length === 0 ? (
              // Removed "dark:text-gray-400"
              <p className="text-gray-600">{t('no_deadlines')}</p>
            ) : (
              <div className="space-y-3">
                {upcomingdeadlines.map((project) => {
                  const daysLeft = Math.ceil((new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    // Removed "dark:bg-gray-700"
                    <div key={project.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-3">
                      <div className="flex items-center space-x-3">
                        {daysLeft <= 2 ? (
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        ) : daysLeft <= 5 ? (
                          <Calendar className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        )}
                        <div>
                          {/* Removed "dark:text-white" */}
                          <p className="font-medium text-gray-900">{project.name}</p>
                          {/* Removed "dark:text-gray-400" */}
                          <p className="text-sm text-gray-600">
                            Due: {formatDate(project.endDate)}
                          </p>
                        </div>
                      </div>
                      {/* Removed "dark:" classes from span */}
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full w-fit ${daysLeft <= 2 ? 'bg-red-100 text-red-800' :
                        daysLeft <= 5 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                        {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Quick Actions & Team */}
        <div className="space-y-6">
          {/* Quick Actions - Updated with navigate */}
          {/* Removed "dark:bg-gray-800" */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            {/* Removed "dark:text-white" */}
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/projects')}
                className="w-full flex items-center justify-center space-x-3 p-3 rounded-lg text-white bg-blue-500 hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>{t('new_project')}</span>
              </button>
              <button
                onClick={() => navigate('/my-team')}
                className="w-full flex items-center justify-center space-x-3 p-3 rounded-lg text-white bg-green-500 hover:bg-green-600 transition-colors"
              >
                <Users className="w-5 h-5" />
                <span>{t('manage_team')}</span>
              </button>
              <button
                onClick={() => navigate('/projects')} // No timeline route, so linking to projects
                className="w-full flex items-center justify-center space-x-3 p-3 rounded-lg text-white bg-purple-500 hover:bg-purple-600 transition-colors"
              >
                <Calendar className="w-5 h-5" />
                <span>{t('view_projects')}</span>
              </button>
              <button
                onClick={() => navigate('/finance/reports')}
                className="w-full flex items-center justify-center space-x-3 p-3 rounded-lg text-white bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                <TrendingUp className="w-5 h-5" />
                <span>{t('view_reports')}</span>
              </button>
            </div>
          </div>

          {/* Team Overview */}
          {/* Removed "dark:bg-gray-800" */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            {/* Removed "dark:text-white" */}
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Team Overview
            </h3>
            {teamMembers.length === 0 ? (
              // Removed "dark:text-gray-400"
              <p className="text-gray-600">{t('no_team_members')}</p>
            ) : (
              <div className="space-y-3">
                {teamMembers
                  .slice(0, 4)
                  .map(consultant => {
                    const assignedProjects = pmProjects.filter(p =>
                      p.consultantId === consultant.id
                    );
                    return (
                      // Removed "dark:bg-gray-700"
                      <div key={consultant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {consultant.name.charAt(0)}
                          </div>
                          <div>
                            {/* Removed "dark:text-white" */}
                            <p className="font-medium text-gray-900">{consultant.name}</p>
                            {/* Removed "dark:text-gray-400" */}
                            <p className="text-sm text-gray-600">
                              {assignedProjects.length} project{assignedProjects.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        {/* Removed "dark:text-green-400" */}
                        <span className="text-sm text-green-600">
                          {consultant.status || 'Available'}
                        </span>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectManagerDashboard;
